import * as React from "react";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import PickersDay, { PickersDayProps } from "@mui/lab/PickersDay";
import endOfWeek from "date-fns/endOfWeek";
import isSameDay from "date-fns/isSameDay";
import isWithinInterval from "date-fns/isWithinInterval";
import startOfWeek from "date-fns/startOfWeek";
import DesktopDatePicker from "@mui/lab/DatePicker";
import DateFnsUtils from "@date-io/date-fns";
import jaLocale from "date-fns/locale/ja";
import format from "date-fns/format";
import moment from "moment";

class ExtendedUtils extends DateFnsUtils {
    getCalendarHeaderText(date) {
        return format(date, "yyyy MMM", { locale: this.locale });
    }
    getDatePickerHeaderText(date) {
        return format(date, "MMMd日", { locale: this.locale });
    }
}

type CustomPickerDayProps = PickersDayProps<Date> & {
    dayIsBetween: boolean;
    isFirstDay: boolean;
    isLastDay: boolean;
    isToday: boolean;
};

const CustomPickersDay = styled(PickersDay, {
    shouldForwardProp: (prop) =>
        !["dayIsBetween", "isFirstDay", "isLastDay", "isToday"].includes(
            prop.toString()
        ),
})<CustomPickerDayProps>(
    ({ theme, dayIsBetween, isFirstDay, isLastDay, isToday }) => ({
        ...(isToday && {
            borderRadius: "15%",
            backgroundColor: theme.palette.common.white,
        }),
        ...(dayIsBetween && {
            borderRadius: 0,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            "&:hover, &:focus": {
                backgroundColor: theme.palette.primary.dark,
            },
        }),
        ...(isFirstDay && {
            borderTopLeftRadius: "15%",
            borderBottomLeftRadius: "15%",
        }),
        ...(isLastDay && {
            borderTopRightRadius: "15%",
            borderBottomRightRadius: "15%",
        }),
        ...(isToday && {
            backgroundColor: theme.palette.common.white,
        }),
    })
) as React.ComponentType<CustomPickerDayProps>;

interface Props {
    onChangeMethod: any;
    initialDate: string;
}

export const WeekPicker: React.FC<Props> = (props: Props) => {
    const [value, setValue] = React.useState<Date | null>(
        new Date(props.initialDate)
    );

    const renderWeekPickerDay = (
        date: Date,
        selectedDates: Array<Date | null>,
        pickersDayProps: PickersDayProps<Date>
    ) => {
        if (!value) {
            return <PickersDay {...pickersDayProps} />;
        }

        const start = startOfWeek(value, { weekStartsOn: 1 });
        const end = endOfWeek(value, { weekStartsOn: 1 });

        const dayIsBetween = isWithinInterval(date, { start, end });
        const isFirstDay = isSameDay(date, start);
        const isLastDay = isSameDay(date, end);
        const isToday = isSameDay(date, new Date());

        return (
            <CustomPickersDay
                {...pickersDayProps}
                disableMargin
                dayIsBetween={dayIsBetween}
                isFirstDay={isFirstDay}
                isLastDay={isLastDay}
                isToday={isToday}
            />
        );
    };

    const [open, setOpen] = React.useState(false);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} locale={jaLocale}>
            <MuiPickersUtilsProvider utils={ExtendedUtils} locale={jaLocale}>
                <DesktopDatePicker
                    label=""
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    value={value}
                    onChange={(ev) => {
                        props.onChangeMethod(ev);
                        const out = moment(ev).format("YYYY-MM-DD");
                        const mon = startOfWeek(ev || 1, { weekStartsOn: 1 });
                        setValue(mon);
                    }}
                    mask="__月__日の週"
                    showDaysOutsideCurrentMonth
                    renderDay={renderWeekPickerDay}
                    renderInput={(params) => (
                        <TextField
                            variant="filled"
                            size="small"
                            onClick={() => {
                                setOpen(true);
                            }}
                            sx={{
                                ".MuiFilledInput-input": {
                                    pt: 3.5,
                                    pb: 3.5,
                                },
                            }}
                            {...params}
                        />
                    )}
                    inputFormat="MM月dd'日の週"
                />
            </MuiPickersUtilsProvider>
        </LocalizationProvider>
    );
};
